import { apiRequest, downloadFile } from '../lib/api-client';

/** Typed access to the CRM search, board and view endpoints. */

export interface FieldDescriptor {
  key: string;
  label: string;
  type: string;
  sortable: boolean;
  options?: string[];
}

export interface SearchCursor {
  value: string | number | null;
  id: string;
}

export interface FilterCondition {
  field: string;
  operator: string;
  value?: unknown;
  values?: unknown[];
}

export interface FilterGroup {
  combinator: 'AND' | 'OR';
  conditions: (FilterCondition | FilterGroup)[];
}

/**
 * How much of the tenant the person who asked is allowed to see.
 *
 * `ALL` is everything, `OWN` only their own records, `TEAMS` their teams', `NONE` nothing. Sent with
 * every listing so an empty table can say which of the two it is: nothing exists, or nothing that
 * belongs to you.
 */
export type Visibility = 'ALL' | 'OWN' | 'TEAMS' | 'NONE';

export interface SearchResponse<Row> {
  results: Row[];
  next_cursor: SearchCursor | null;
  total: number | null;
  visibility?: Visibility;
}

export interface CompanyRow {
  id: string;
  name: string;
  industry: string | null;
  email: string | null;
  employee_count: number | null;
  /** Entries from the tenant's own catalogues; the names are resolved on screen. */
  status_id: string | null;
  source_id: string | null;
  score: number | string | null;
  created_at: string;
}

export interface BoardCard {
  id: string;
  title: string;
  amount_cents: number | string;
  company_id: string | null;
  owner_id: string | null;
  expected_close_date: string | null;
  source_id: string | null;
  /** When it last moved: what "three days in this stage" is measured from. */
  stage_changed_at: string | null;
  version: number;
}

export interface BoardColumn {
  stage: {
    id: string;
    name: string;
    position: number;
    probability: number;
    is_won: boolean;
    is_lost: boolean;
  };
  count: number;
  amount_cents: number;
  cards: BoardCard[];
}

export interface SavedView {
  id: string;
  entity_type: string;
  name: string;
  view_type: string;
  filter: FilterGroup;
  visibility: string;
  is_default: boolean;
}

export function listFields(entity: string): Promise<{ fields: FieldDescriptor[] }> {
  return apiRequest(`/v1/crm/${entity}/fields`);
}

export function searchEntity<Row>(
  entity: string,
  body: {
    filter?: FilterGroup;
    sort?: { field: string; direction: 'asc' | 'desc' };
    limit?: number;
    cursor?: SearchCursor;
    includeCount?: boolean;
  },
): Promise<SearchResponse<Row>> {
  return apiRequest(`/v1/crm/${entity}/search`, { method: 'POST', body });
}

export function loadBoard(
  body: { filter?: FilterGroup; pipelineId?: string; perColumn?: number } = {},
): Promise<{ pipeline_id: string; columns: BoardColumn[] }> {
  return apiRequest('/v1/crm/opportunities/board', { method: 'POST', body });
}

/**
 * Moves a deal to another stage.
 *
 * `expectedVersion` is what stops two people dragging the same card at once: the second move is
 * refused rather than silently overwriting the first.
 */
export function moveDeal(
  id: string,
  body: { stageId: string; expectedVersion: number; lostReason?: string | null },
): Promise<{ id: string }> {
  return apiRequest(`/v1/crm/opportunities/${id}/stage`, { method: 'POST', body });
}

export function deleteDeal(id: string): Promise<void> {
  return apiRequest(`/v1/crm/opportunities/${id}`, { method: 'DELETE' });
}

export function createDeal(body: {
  title: string;
  pipelineId: string;
  stageId: string;
  amountCents?: number;
}): Promise<{ id: string }> {
  return apiRequest('/v1/crm/opportunities', { method: 'POST', body });
}

export function listViews(entityType: string): Promise<{ views: SavedView[] }> {
  return apiRequest(`/v1/views?entityType=${entityType}`);
}

export function saveView(body: {
  entityType: string;
  name: string;
  viewType?: string;
  filter?: FilterGroup;
  visibility?: string;
}): Promise<SavedView> {
  return apiRequest('/v1/views', { method: 'POST', body });
}

/** Money is minor units on the wire; formatting belongs at the edge. */
export function formatMoney(cents: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(cents / 100);
}

/**
 * The rows behind a list, as a CSV.
 *
 * The same filter the list is showing, so the file and the screen agree. The server applies the
 * same permission and record scope and audits the export: a copy of a tenant's customer list
 * leaving the product is exactly the action somebody asks about afterwards.
 */
export function exportEntity(
  entity: 'companies' | 'contacts' | 'leads' | 'opportunities',
  params: { filter?: FilterGroup; sort?: { field: string; direction: 'asc' | 'desc' } } = {},
): Promise<{ truncated: boolean }> {
  return downloadFile(`/v1/crm/${entity}/export`, {
    body: params,
    fallbackName: `${entity}.csv`,
  });
}

/**
 * The two questions a sales screen should answer (Phase 120).
 *
 * `attention` is what somebody has let slip; `forecast` is what the month is going to be. Both are
 * scoped on the server exactly as the board is, so a rep sees their own deals in both.
 */
export type AttentionReason =
  'STUCK' | 'OVERDUE' | 'CLOSING_BLIND' | 'SILENT' | 'NO_NEXT_STEP' | 'NO_CONTACT' | 'UNOWNED';

export interface DealAttention {
  id: string;
  title: string;
  amountCents: number;
  stageName: string;
  reasons: AttentionReason[];
  idleDays: number;
  score: number;
}

export function listDealsNeedingAttention(
  limit = 20,
): Promise<{ deals: DealAttention[]; scanned: number }> {
  return apiRequest(`/v1/crm/opportunities/attention?limit=${limit}`);
}

export interface ForecastMonth {
  month: string;
  wonCents: number;
  weightedCents: number;
  bestCaseCents: number;
  openCount: number;
  wonCount: number;
}

export interface SalesForecast {
  from: string;
  months: ForecastMonth[];
  totals: {
    wonCents: number;
    weightedCents: number;
    bestCaseCents: number;
    coverage: number | null;
  };
  targetCents: number | null;
}

export function getForecast(months = 3): Promise<SalesForecast> {
  return apiRequest(`/v1/crm/opportunities/forecast?months=${months}`);
}

/** The dates a relationship is made of (Phase 121). */
export type KeyDateKind = 'BIRTHDAY' | 'ANNIVERSARY' | 'RENEWAL' | 'CONTRACT_END' | 'CUSTOM';

export interface KeyDate {
  /** Null for a birthday: it is a column on the contact, not a row that can be deleted here. */
  id: string | null;
  entityType: string;
  entityId: string;
  kind: KeyDateKind;
  label: string;
  subject: string | null;
  occurrence: string;
  daysAway: number;
  when: 'TODAY' | 'TOMORROW' | 'SOON' | 'PAST';
  yearly: boolean;
  ownerId: string | null;
  notes: string | null;
}

export function listKeyDates(days = 30): Promise<{ dates: KeyDate[] }> {
  return apiRequest(`/v1/crm/key-dates?days=${days}`);
}

export function listKeyDatesOf(
  entityType: string,
  entityId: string,
): Promise<{ dates: KeyDate[] }> {
  return apiRequest(`/v1/crm/${entityType}/${entityId}/key-dates`);
}

export function addKeyDate(body: {
  entityType: string;
  entityId: string;
  kind?: KeyDateKind;
  label: string;
  happensOn: string;
  yearly?: boolean;
  remindDays?: number;
  notes?: string | null;
}): Promise<KeyDate> {
  return apiRequest('/v1/crm/key-dates', { method: 'POST', body });
}

export function removeKeyDate(id: string): Promise<void> {
  return apiRequest(`/v1/crm/key-dates/${id}`, { method: 'DELETE' });
}

/** Prospecting cadences (Phase 122). */
export type CadenceStepKind = 'EMAIL' | 'TASK';

export interface CadenceStep {
  position: number;
  dayOffset: number;
  kind: CadenceStepKind;
  subject: string;
  body: string;
  channel: string | null;
}

export interface Cadence {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  stopOnReply: boolean;
  steps: CadenceStep[];
  createdAt: string;
}

export interface CadenceEnrollment {
  id: string;
  cadenceId: string;
  entityType: 'LEAD' | 'CONTACT';
  entityId: string;
  status: 'ACTIVE' | 'FINISHED' | 'STOPPED' | 'FAILED';
  lastStep: number;
  nextDueAt: string | null;
  startedAt: string;
  stopReason: string | null;
}

export function listCadences(): Promise<{ cadences: Cadence[] }> {
  return apiRequest('/v1/crm/cadences');
}

export function createCadence(body: {
  name: string;
  description?: string | null;
  stopOnReply?: boolean;
  steps: Omit<CadenceStep, 'position'>[];
}): Promise<Cadence> {
  return apiRequest('/v1/crm/cadences', { method: 'POST', body });
}

export function updateCadence(
  id: string,
  body: Partial<{
    name: string;
    description: string | null;
    stopOnReply: boolean;
    isActive: boolean;
    steps: Omit<CadenceStep, 'position'>[];
  }>,
): Promise<Cadence> {
  return apiRequest(`/v1/crm/cadences/${id}`, { method: 'PATCH', body });
}

export function removeCadence(id: string): Promise<void> {
  return apiRequest(`/v1/crm/cadences/${id}`, { method: 'DELETE' });
}

export function listEnrollments(cadenceId: string): Promise<{ enrollments: CadenceEnrollment[] }> {
  return apiRequest(`/v1/crm/cadences/${cadenceId}/enrollments`);
}

export function enrollInCadence(
  cadenceId: string,
  body: { entityType: 'LEAD' | 'CONTACT'; entityId: string },
): Promise<CadenceEnrollment> {
  return apiRequest(`/v1/crm/cadences/${cadenceId}/enroll`, { method: 'POST', body });
}

export function stopEnrollment(id: string): Promise<void> {
  return apiRequest(`/v1/crm/cadence-enrollments/${id}/stop`, { method: 'POST', body: {} });
}

/** Orders, with the states written down (Phase 123). */
export type OrderStatus =
  | 'DRAFT'
  | 'CONFIRMED'
  | 'IN_PRODUCTION'
  | 'READY'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'CANCELLED'
  | 'RETURNED';

export interface OrderItem {
  productId: string | null;
  description: string;
  quantity: number;
  unitPriceCents: number;
  discountBps: number;
}

export interface Order {
  id: string;
  number: number;
  companyId: string | null;
  contactId: string | null;
  opportunityId: string | null;
  status: OrderStatus;
  statusChangedAt: string;
  /** Where it may go from here: the screen offers only these. */
  nextStatuses: OrderStatus[];
  currency: string;
  totalCents: number;
  expectedDate: string | null;
  deliveredAt: string | null;
  cancelledReason: string | null;
  notes: string | null;
  ownerId: string | null;
  items: OrderItem[];
  createdAt: string;
}

export function listOrders(status?: OrderStatus): Promise<{ orders: Order[] }> {
  return apiRequest(`/v1/crm/orders${status ? `?status=${status}` : ''}`);
}

export function createOrder(body: {
  companyId?: string | null;
  contactId?: string | null;
  opportunityId?: string | null;
  expectedDate?: string | null;
  notes?: string | null;
  items?: Omit<OrderItem, 'productId'>[];
}): Promise<Order> {
  return apiRequest('/v1/crm/orders', { method: 'POST', body });
}

export function moveOrder(
  id: string,
  body: { status: OrderStatus; reason?: string | null },
): Promise<Order> {
  return apiRequest(`/v1/crm/orders/${id}/status`, { method: 'POST', body });
}

export function removeOrder(id: string): Promise<void> {
  return apiRequest(`/v1/crm/orders/${id}`, { method: 'DELETE' });
}
